/**
 * Session query operations
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  type QueryConstraint
} from 'firebase/firestore'
import { db } from '../../firebase'
import { formatDate } from '../../utils/dateUtils'
import {
  SESSIONS_COLLECTION,
  type SessionQueryOptions,
  type PaginatedSessionQueryOptions,
  type PaginatedResult,
  type SessionDocument,
  type SessionWithId
} from './types'

/**
 * Get session by ID
 */
export async function getSession(sessionId: string): Promise<SessionWithId | null> {
  const sessionDoc = await getDoc(doc(db, SESSIONS_COLLECTION, sessionId))

  if (!sessionDoc.exists()) {
    return null
  }

  return {
    id: sessionDoc.id,
    ...sessionDoc.data() as SessionDocument
  }
}

/**
 * Get today's active session for a user
 */
export async function getTodayActiveSession(userId: string): Promise<SessionWithId | null> {
  const today = formatDate(new Date())

  const q = query(
    collection(db, SESSIONS_COLLECTION),
    where('userId', '==', userId),
    where('date', '==', today),
    where('status', '==', 'active'),
    limit(1)
  )

  const snapshot = await getDocs(q)

  if (snapshot.empty) {
    return null
  }

  const firstDoc = snapshot.docs[0]
  if (!firstDoc) {
    return null
  }

  return {
    id: firstDoc.id,
    ...firstDoc.data() as SessionDocument
  }
}

/**
 * Get sessions for a date range
 */
export async function getSessions(options: SessionQueryOptions = {}): Promise<SessionWithId[]> {
  const { userId, startDate, endDate, status, limit: limitCount } = options
  const constraints: QueryConstraint[] = []

  if (userId) {
    constraints.push(where('userId', '==', userId))
  }

  if (startDate) {
    constraints.push(where('date', '>=', startDate))
  }

  if (endDate) {
    constraints.push(where('date', '<=', endDate))
  }

  if (status) {
    constraints.push(where('status', '==', status))
  }

  constraints.push(orderBy('date', 'desc'))

  if (limitCount) {
    constraints.push(limit(limitCount))
  }

  const q = query(collection(db, SESSIONS_COLLECTION), ...constraints)
  const snapshot = await getDocs(q)

  return snapshot.docs.map(docSnap => ({
    id: docSnap.id,
    ...docSnap.data() as SessionDocument
  }))
}

/**
 * Get sessions with cursor-based pagination
 */
export async function getSessionsPaginated(
  options: PaginatedSessionQueryOptions = {}
): Promise<PaginatedResult<SessionWithId>> {
  const { userId, startDate, endDate, status, pageSize = 20, cursor } = options
  const constraints: QueryConstraint[] = []

  if (userId) {
    constraints.push(where('userId', '==', userId))
  }

  if (startDate) {
    constraints.push(where('date', '>=', startDate))
  }

  if (endDate) {
    constraints.push(where('date', '<=', endDate))
  }

  if (status) {
    constraints.push(where('status', '==', status))
  }

  constraints.push(orderBy('date', 'desc'))

  // Add cursor for pagination
  if (cursor) {
    constraints.push(startAfter(cursor))
  }

  // Request one extra to check if there are more
  constraints.push(limit(pageSize + 1))

  const q = query(collection(db, SESSIONS_COLLECTION), ...constraints)
  const snapshot = await getDocs(q)

  const hasMore = snapshot.docs.length > pageSize
  const docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs

  return {
    data: docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data() as SessionDocument
    })),
    lastDoc: docs.length > 0 ? docs[docs.length - 1] ?? null : null,
    hasMore
  }
}

/**
 * Get count of flagged sessions for a date
 * Uses Firestore's 'in' operator for efficient multi-status query
 */
export async function getFlaggedSessionsCount(date: string): Promise<number> {
  const q = query(
    collection(db, SESSIONS_COLLECTION),
    where('date', '==', date),
    where('status', 'in', ['flagged', 'auto-closed'])
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.length
}
